import { useEffect } from "react"
import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { STATUS_CONFIG } from "@/lib/project-configs"
import type { PipeStatusRecord, ProjectStatus } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  pipeStatus?: PipeStatusRecord
  defaultParentStatus?: ProjectStatus
}

const PARENT_STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: "pending", label: STATUS_CONFIG.pending.label },
  { value: "accepted", label: STATUS_CONFIG.accepted.label },
  { value: "rejected", label: STATUS_CONFIG.rejected.label },
  { value: "asset", label: STATUS_CONFIG.asset.label },
  { value: "archived", label: STATUS_CONFIG.archived.label },
]

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
}

export function PipeStatusFormDialog({ open, onOpenChange, pipeStatus, defaultParentStatus }: Props) {
  const isEditing = !!pipeStatus

  const { data, setData, post, patch, processing, reset } = useForm({
    name: pipeStatus?.name || "",
    label: pipeStatus?.label || "",
    parent_status: pipeStatus?.parent_status || defaultParentStatus || "pending",
    bg_color: pipeStatus?.bg_color || "#e0e7ff",
    text_color: pipeStatus?.text_color || "#3730a3",
  })

  // Sync form data when pipeStatus or defaultParentStatus changes
  useEffect(() => {
    setData({
      name: pipeStatus?.name || "",
      label: pipeStatus?.label || "",
      parent_status: pipeStatus?.parent_status || defaultParentStatus || "pending",
      bg_color: pipeStatus?.bg_color || "#e0e7ff",
      text_color: pipeStatus?.text_color || "#3730a3",
    })
  }, [pipeStatus, defaultParentStatus])

  const handleLabelChange = (value: string) => {
    setData("label", value)
    if (!isEditing) {
      setData("name", slugify(value))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const onSuccess = () => {
      onOpenChange(false)
      if (!isEditing) reset()
    }
    if (isEditing) {
      patch(`/pipe_statuses/${pipeStatus.id}`, { onSuccess })
    } else {
      post("/pipe_statuses", { onSuccess })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Modifier le statut" : "Nouveau statut"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ps_label">Libellé</Label>
            <Input
              id="ps_label"
              value={data.label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="Ex: A contacter"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ps_name">Identifiant</Label>
            <Input
              id="ps_name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              placeholder="Ex: to_contact"
              required
              className="font-mono text-sm"
            />
          </div>

          {!isEditing && (
            <div className="space-y-2">
              <Label>Statut parent</Label>
              <Select value={data.parent_status} onValueChange={(v) => setData("parent_status", v as ProjectStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARENT_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ps_bg_color">Couleur de fond</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="ps_bg_color"
                  value={data.bg_color}
                  onChange={(e) => setData("bg_color", e.target.value)}
                  className="size-9 cursor-pointer rounded border"
                />
                <Input
                  value={data.bg_color}
                  onChange={(e) => setData("bg_color", e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ps_text_color">Couleur du texte</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="ps_text_color"
                  value={data.text_color}
                  onChange={(e) => setData("text_color", e.target.value)}
                  className="size-9 cursor-pointer rounded border"
                />
                <Input
                  value={data.text_color}
                  onChange={(e) => setData("text_color", e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Aperçu</Label>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium"
                style={{ backgroundColor: data.bg_color, color: data.text_color }}
              >
                {data.label || "Statut"}
              </span>
            </div>
          </div>

          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              {isEditing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
